import { scaleOrdinal } from "d3-scale";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import ColorLegend from "../charts/lib/ColorLegend";
import { withFigureIO } from "../components/figure";
import { useReadScrolly } from "../components/scrolly";
import { useConfig } from "../config";
import { metadata } from "../data/data_access_fig_OOSRATE";
import { datasets } from "../data/data_access_fig_OOSRATE_datasets";
import { countryIdIso, foldGeoEntity, getCountryMeta, getCountryName, mkGemEntityDecoder, useCountryEntities, useFigureControlItems, useMultiSelectState, useNamedEntitiesWithLevel } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { fromCount, mkScrollyColorPalette } from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, io, O, pipe } from "../prelude";
export * from "../data/data_access_fig_OOSRATE";
const DEFAULT_COUNTRIES = [
    "BGD",
    "CIV",
    "CRI"
];
export const Data = mkGemEntityDecoder([
    "value",
    "year",
    "level"
], {
    type: io.string
});
export const Chart = ({ data })=>{
    const env = useConfig();
    const intl = useIntl();
    const screenMDown = !useMedia(breakpoints.m);
    const [selectedCountries, actions] = useMultiSelectState("countries");
    const { activeSection, sections } = useReadScrolly();
    const scrollyCountries = React.useMemo(()=>pipe(activeSection, O.map((x)=>x.split(",")), O.chain((strIds)=>Ar.array.sequence(O.option)(strIds.map((strId)=>getCountryMeta(env.countries, countryIdIso.wrap(strId))))), O.map((xs)=>xs.map((meta)=>meta.iso3c)), O.getOrElse(()=>DEFAULT_COUNTRIES.map(countryIdIso.wrap))), [
        env.countries,
        activeSection
    ]);
    const entityData = useNamedEntitiesWithLevel(data);
    const countryData = useCountryEntities(entityData);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    React.useEffect(()=>{
        actions.setSelectionControl("countries", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Countries",
                defaultMessage: "Countries"
            })),
            selected: O.some(DEFAULT_COUNTRIES)
        }, countryControlItems);
    }, [
        intl,
        actions,
        countryControlItems
    ]);
    React.useEffect(()=>{
        if (O.isSome(activeSection)) {
            actions.updateSelectionControl("countries", O.some(scrollyCountries.map((x)=>countryIdIso.unwrap(x))));
        }
    }, [
        intl,
        actions,
        activeSection,
        scrollyCountries
    ]);
    const chartData = React.useMemo(()=>{
        return entityData.filter(foldGeoEntity({
            country: ({ id })=>pipe(selectedCountries, O.fold(()=>false, (xs)=>xs.includes(countryIdIso.unwrap(id)))),
            region: ({ entity_type })=>entity_type === "world"
        }));
    }, [
        entityData,
        selectedCountries
    ]);
    const selectedCountryLabels = React.useMemo(()=>{
        return pipe(selectedCountries, O.map((xs)=>xs.map((x)=>getCountryName(env.countries, countryIdIso.wrap(x)))));
    }, [
        env.countries,
        selectedCountries
    ]);
    const { colorScale, colorLegendValues } = React.useMemo(()=>{
        const multiIdO = pipe(selectedCountries, O.map((xs)=>xs.join(",")));
        const count = pipe(multiIdO, O.fold(()=>1, (id)=>id.split(",").length));
        const palette = fromCount(mkScrollyColorPalette(Array.from(sections.values()), multiIdO), count);
        const colorScale = scaleOrdinal(palette).domain(pipe(selectedCountryLabels, O.getOrElse(()=>[])));
        const colorLegendValues = colorScale.domain().map((label)=>({
                label,
                color: colorScale(label)
            }));
        return {
            colorScale,
            colorLegendValues
        };
    }, [
        sections,
        selectedCountries,
        selectedCountryLabels
    ]);
    return <>
      <LineChart tLabel={(s)=>s} height={screenMDown ? M.chartHeight.xs : M.chartHeight.m} endLabel={!screenMDown} x="year" timeParse="%Y" timeFormat="%Y" numberFormat=".1%" column="level_name" columns={3} columnSort="none" category="datum.entity_name" colorLegend={false} colorScale={colorScale} values={chartData} yTicks={[
        0,
        0.5,
        1
    ]} connectLines showTooltip markStyle={(x)=>x.entity_type === "world" ? "reference" : "normal"}/>
      <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>
    </>;
};
export default withFigureIO({
    url: new URL("../data/data_access_fig_OOSRATE.json", import.meta.url).href,
    csv: new URL("../data/data_access_fig_OOSRATE.zip", import.meta.url).href,
    xlsx: new URL("../data/data_access_fig_OOSRATE.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
